
import Vue from 'vue';
import { db, QueryDocumentSnapshot } from '@/firebase';
import _find from 'lodash/find';
import _keys from 'lodash/keys';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import { mapActions, mapGetters, mapState } from 'vuex';
import pluralize from 'pluralize';
import { ClubOffer, Company, Team, User } from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import TeamCard from '@/components/TeamCard.vue';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import { fuzzyFilter } from '@/utils/fuzzySearch';

const PAGE_SIZE = 100;

// Resolves a raw team doc's DocumentReference fields (company + company.clubOffer) into
// fully-populated nested objects, matching what Vuefire's $bind would do at maxRefDepth=2.
// We do this manually because this component intentionally uses .get() (not $bind) —
// the picker doesn't need realtime data.
async function resolveTeamRefs (rawTeam: Team, teamId: string): Promise<Team> {
	const team: Team = { ...rawTeam, id: teamId };
	if (!team.companyId) {
		team.company = null;
		return team;
	}
	const companyRef = db.doc(`companies/${team.companyId}`);
	const companyData = (await companyRef.get()).data() as Company | undefined;
	if (!companyData) {
		team.company = null;
		return team;
	}
	const company: Company = { ...companyData, id: companyRef.id };
	if (company.club) {
		const clubOfferRef = db.doc(`clubOffers/${company.club}`);
		const clubOfferData = (await clubOfferRef.get()).data() as ClubOffer | undefined;
		if (clubOfferData) {
			company.clubOffer = { ...clubOfferData, id: clubOfferRef.id };
		}
	}
	team.company = company;
	return team;
}

export default Vue.extend({
	name: 'TeamPicker',
	props: {
		eager: { type: Boolean, default: false },
		selectedTeam: { type: Object, default: null }, // Can use this if `localValOnly` is true
		selectedCompany: { type: Object, default: null }, // Can use this if `localValOnly` is true
		defaultTeamId: { type: String, default: null }, // Can use this if `localValOnly` is true
		defaultCompanyId: { type: String, default: null }, // Can use this if `localValOnly` is true
		onlyCurrCompanyTeams: { type: Boolean, default: false }, // Can use this if `localValOnly` is true
		allowCompanySelection: { type: Boolean, default: false }, // Can use this if `localValOnly` is true
		localValOnly: { type: Boolean, default: false },

		openTilSelectionIsMade: { type: Boolean, default: false },
	},
	data () {
		return {
			dialogValue: false,

			awaitingSelectionTeamId: null,
			awaitingSelectionTimeout: null,

			// All teams loaded so far. Snapshot-based — not realtime. Filled by paged .get()s
			// against teams.where('userIds.${uid}','==',true). Each entry has company + company.clubOffer
			// manually resolved by resolveTeamRefs() before being pushed in.
			teamsList: [] as Team[],

			// Pagination cursor (last QueryDocumentSnapshot from the previous page) + can-more flag.
			lastSnap: null as QueryDocumentSnapshot | null,
			canLoadMore: false,
			hasLoadedInitial: false,

			loadingInitial: false,
			loadingMore: false,

			searchValue: '',
		};
	},
	computed: {
		...mapState([
			'team',
			'user',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// Derived from teamsList — each team.company.clubOffer is already a resolved object
		// (see resolveTeamRefs()).
		companiesList (): Company[] {
			const companies = (this.teamsList || [])
				.map((t: Team) => t.company)
				.filter((c) => !!c && typeof c !== 'string') as Company[];
			return _uniqBy(companies, 'id');
		},
		currentTeamId () {
			if (this.localValOnly) {
				return this._get(this.selectedTeam, 'id', null);
			}
			return this._get(this.team, 'id', null);
		},
		currentCompanyId () {
			if (this.localValOnly && this.selectedCompany) {
				return this._get(this.selectedCompany, 'id', null);
			}
			return this._get(this.team, 'companyId', null);
		},
		showTeamPicker () {
			const numTeams: number = _keys(this.user?.teamIds || {}).length;
			return numTeams > 1;
		},
		dialogPersistent () {
			return !!(this.openTilSelectionIsMade && !this.team);
		},
		dialogOpen () {
			return !!(
				this.dialogValue ||
				(this.openTilSelectionIsMade && !this.team)
			);
		},
		shouldInitTeamsList () {
			return !!(this.user?.id && (this.eager || this.dialogOpen));
		},
		showSearch () {
			return this.teamsList.length > 3;
		},
		// Display-time name sort (no firestore orderBy — `userIds.${uid}` is a dynamic field
		// path and can't be backed by a composite index).
		sortedTeamsList (): Team[] {
			return _sortBy(this.teamsList, [(t: Team) => (t.name || '').toLowerCase()]);
		},
		filteredTeamsList () {
			if (!this.searchValue) return this.sortedTeamsList;

			return fuzzyFilter(
				this.sortedTeamsList,
				this.searchValue,
				[
					{ name: 'name', weight: 0.4 },
					{ name: 'company.name', weight: 0.6 },
				],
			);
		},
		filteredCompaniesList () {
			if (!this.searchValue) return this.companiesList;

			return fuzzyFilter(
				this.companiesList,
				this.searchValue,
				[
					{ name: 'name', weight: 1 },
				],
			);
		},
	},
	mounted () {
		this.initIfReady(this.shouldInitTeamsList, this.user);
	},
	destroyed () {
		if (this.awaitingSelectionTimeout) clearTimeout(this.awaitingSelectionTimeout);
	},
	watch: {
		shouldInitTeamsList (newVal: boolean, oldVal: boolean) {
			if (newVal !== oldVal) {
				this.initIfReady(newVal, this.user);
			}
		},
		user (newVal: User, oldVal: User) {
			// If the user changes (e.g., logout/login or admin proxy switch), re-fetch for the new uid.
			const newId: string = newVal?.id || null;
			const oldId: string = oldVal?.id || null;
			if (newId !== oldId) {
				this.resetState();
				this.initIfReady(this.shouldInitTeamsList, newVal);
			}
		},
		team (newVal: Team) {
			// Only handle team change in store mode, not in controlled mode.
			if (!this.localValOnly && this.awaitingSelectionTeamId && newVal && this._get(newVal, 'id') === this.awaitingSelectionTeamId) {
				if (this.awaitingSelectionTimeout) clearTimeout(this.awaitingSelectionTimeout);
				this.awaitingSelectionTeamId = null;
				this.onClose();
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'bindAllTeamData',
			'unbindAllTeamData',
		]),
		pluralize,
		onClose () {
			this.dialogValue = false;
		},
		// --- Load lifecycle -------------------------------------------------------------
		resetState () {
			this.teamsList = [];
			this.lastSnap = null;
			this.canLoadMore = false;
			this.hasLoadedInitial = false;
		},
		async initIfReady (shouldInit: boolean, user: User) {
			if (!shouldInit || this.hasLoadedInitial || this.loadingInitial) return;
			if (!user?.id) return;
			this.loadingInitial = true;
			try {
				await this.fetchPage();
				this.hasLoadedInitial = true;

				// Auto-focus search field once we have rows.
				if (this.showSearch) {
					this.$nextTick(() => {
						setTimeout(() => {
							const searchField = this.$refs.searchField as { focus?: () => void } | undefined;
							searchField?.focus?.();
						}, 300);
					});
				}

				this.applyDefaultsIfApplicable();
			} catch (e) {
				console.error('[TeamPicker] failed to load initial teams', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading ${pluralize(this.displayLabels.team)}, please reach out to report issues by going to Resources -> Help from the menu options`,
					show: true,
				});
			} finally {
				this.loadingInitial = false;
			}
		},
		async loadMoreTeams () {
			if (!this.canLoadMore || this.loadingMore) return;
			this.loadingMore = true;
			try {
				await this.fetchPage();
				this.applyDefaultsIfApplicable();
			} catch (e) {
				console.error('[TeamPicker] failed to load more teams', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error loading more ${pluralize(this.displayLabels.team)}`,
					show: true,
				});
			} finally {
				this.loadingMore = false;
			}
		},
		// Fetches one page of teams (PAGE_SIZE docs), resolves their company + company.clubOffer
		// in parallel, and appends to `teamsList`. Pagination uses the QueryDocumentSnapshot
		// cursor (`lastSnap`) instead of a field cursor — required because we can't orderBy
		// (the `userIds.${uid}` filter is on a dynamic field path with no composite index).
		async fetchPage () {
			if (!this.user?.id) return;
			let ref = db
				.collection('teams')
				.where(`userIds.${this.user.id}`, '==', true)
				.limit(PAGE_SIZE);
			if (this.lastSnap) ref = ref.startAfter(this.lastSnap);

			const snap = await ref.get();
			if (snap.empty) {
				this.canLoadMore = false;
				return;
			}

			const newTeams: Team[] = await Promise.all(
				snap.docs.map((d: QueryDocumentSnapshot) => resolveTeamRefs(d.data() as Team, d.id)),
			);

			// Dedupe defensively — if a later page somehow overlaps with an earlier one
			// (shouldn't happen with snapshot cursors, but cheap to guard).
			const existing = new Set(this.teamsList.map((t: Team) => t.id));
			const additions = newTeams.filter((t: Team) => !existing.has(t.id));
			this.teamsList = this.teamsList.concat(additions);

			this.lastSnap = snap.docs[snap.docs.length - 1];
			this.canLoadMore = snap.docs.length === PAGE_SIZE;
		},
		// User-triggered refresh: drop everything and re-fetch page 1.
		async onRefreshTeams () {
			if (this.loadingInitial || this.loadingMore) return;
			this.resetState();
			await this.initIfReady(true, this.user);
		},
		// --- Selection ------------------------------------------------------------------
		emitSelection (team: Team, company: Company, companyFirstTeam: Team) {
			if (team || company) {
				this.$emit('on-select', {
					...(company && {
						company,
						companyFirstTeam: companyFirstTeam || null,
					}),
					...(!company && team && {
						team,
					}),
				});
			}
		},
		onCompanySelect (company: Company) {
			const companyFirstTeam: Team = _find(this.teamsList, ['companyId', company.id]) || null;
			this.emitSelection(null, company, companyFirstTeam);
			this.onClose();
		},
		onTeamSelect (team: Team) {
			if (this.awaitingSelectionTimeout) clearTimeout(this.awaitingSelectionTimeout);

			if (this.localValOnly) {
				// In controlled mode, emit selection instead of binding to store.
				this.emitSelection(team, null, null);
				this.onClose();
				return;
			}

			if (team) {
				this.bindAllTeamData(team.id);

				this.awaitingSelectionTeamId = team.id;
				this.awaitingSelectionTimeout = setTimeout(() => {
					this.awaitingSelectionTeamId = null;
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'That team took too long to load. Please try again, or reach out to report issues by going to Resources -> Help from the menu options.',
						show: true,
					});
				}, 10000);
				this.emitSelection(team, null, null);
			} else {
				this.unbindAllTeamData();
			}
		},
		applyDefaultsIfApplicable () {
			if (!this.localValOnly) return;

			// Prefer default company selection if both are provided.
			if (this.defaultCompanyId && !this.selectedCompany) {
				const defaultCompany: Company = _find(this.companiesList, ['id', this.defaultCompanyId]) || null;
				if (defaultCompany) {
					const defaultCompanyFirstTeam: Team = _find(this.teamsList, ['companyId', this.defaultCompanyId]) || null;
					this.emitSelection(null, defaultCompany, defaultCompanyFirstTeam);
					return;
				}
			}
			if (this.defaultTeamId && !this.selectedTeam && !this.selectedCompany) {
				const defaultTeam: Team = _find(this.teamsList, ['id', this.defaultTeamId]) || null;
				if (defaultTeam) {
					this.emitSelection(defaultTeam, null, null);
				}
			}
		},
	},
	components: {
		TeamCard,
		EmptyState,
		LoadingSVG,
		DialogOrBottomSheet,
	},
});
